"use client"

import React, { useState } from "react"
import { Progress } from "@/components/ui/progress"
import { RequestProgressPanel } from "@/components/request-progress-panel"
import type { WorkOrder } from "@/types/workflow"

interface RequestProgressCellProps {
  order: WorkOrder
}

function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map(part => part.charAt(0).toUpperCase())
    .join("")
}

export function RequestProgressCell({ order }: RequestProgressCellProps) {
  const [isPanelOpen, setIsPanelOpen] = useState(false)

  const total = order.requestTasksTotal ?? 0
  const completed = order.requestTasksCompleted ?? 0
  const taskName = order.currentRequestTaskName as string | undefined
  const assignee = order.requestCurrentAssignee as string | undefined
  const fboId = order.fboId as number | undefined
  const entityType = (order.entityType as string) || 'WorkOrder'

  const percent = total > 0 ? Math.round((completed / total) * 100) : 0
  const isEmpty = total === 0
  const initials = assignee ? getInitials(assignee) : null

  return (
    <>
      <button
        className="w-full px-2 py-1 text-left group hover:bg-blue-50 rounded transition-colors"
        onClick={() => setIsPanelOpen(true)}
      >
        {taskName && (
          <p className="text-xs text-gray-500 truncate mb-1 max-w-full" title={assignee ? `${assignee}: ${taskName}` : taskName}>
            {initials && (
              <span className="font-semibold text-gray-700 mr-0.5">{initials}:</span>
            )}
            {taskName}
          </p>
        )}
        <div className="flex items-center gap-2">
          <Progress
            value={percent}
            className="h-3 flex-1 bg-slate-200 [&>div]:bg-blue-400"
          />
          <span className={`text-xs font-semibold whitespace-nowrap tabular-nums ${isEmpty ? "text-gray-400" : "text-gray-700"}`}>
            {completed}/{total}
          </span>
        </div>
      </button>

      <RequestProgressPanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        fboId={fboId}
        entityType={entityType}
        workerName={order.workerFullName as string | undefined}
      />
    </>
  )
}
